<template>
  <div class="documents-page">
    <section class="documents-hero">
      <div class="hero-copy">
        <div class="hero-icon" aria-hidden="true">
          <v-icon color="white">mdi-folder-multiple-outline</v-icon>
        </div>
        <div>
          <div class="hero-eyebrow">Dokumentenablage</div>
          <h1>Dokumente zentral im Griff</h1>
          <p>Ablegen, zuweisen und visieren – übersichtlich an einem Ort.</p>
        </div>
      </div>

      <div class="hero-actions">
        <v-btn outlined rounded class="secondary-action d-md-none" aria-label="Ordnernavigation öffnen" @click="$refs.treeManager.toggleDrawer()">
          <v-icon left small>mdi-file-tree-outline</v-icon>
          Ordner
        </v-btn>

        <v-tooltip bottom v-if="userService.hasRight('d_fetchAllDocs')">
          <template v-slot:activator="{ on, attrs }">
            <v-btn outlined rounded class="secondary-action" v-bind="attrs" v-on="on" aria-label="Neue E-Mails abrufen" @click="loadEmails">
              <v-icon :left="$vuetify.breakpoint.smAndUp">mdi-email-sync-outline</v-icon>
              <span class="d-none d-sm-inline">Mails abrufen</span>
            </v-btn>
          </template>
          <span>Neue Dokumente vom Mailserver abrufen</span>
        </v-tooltip>

        <v-btn v-if="userService.hasRight('d_fetchAllDocs')" color="primary" depressed rounded class="primary-action" @click="uploadMedia">
          <v-icon left>mdi-cloud-upload-outline</v-icon>
          <span>Hochladen</span>
        </v-btn>
      </div>
    </section>

    <!-- Hidden Fetchers/Dialogs -->
    <document-email-fetcher @newAttachements="reloadTreeManager" ref="documentMailFetcher" />

    <file-drop-upload
      :allowed="userService.hasRight('d_fetchAllDocs')"
      @hideDialog="showUploadDialog = false"
      :showUploadDialog="showUploadDialog"
      @fileUpload="reloadTreeManager" />

    <section class="documents-workspace">
      <folder-tree-manager ref="treeManager" />
    </section>
  </div>
</template>

<script>
import userService from "@/services/userService"
import documentEmailFetcher from "./components/documentEmailFetcher.vue"
import fileDropUpload from "./components/fileDropUpload.vue"
import folderTreeManager from "./folderTreeManager.vue"
import api from "@/api" // Stelle sicher, dass hier deine API-Funktion fetchDocumentEmails verfügbar ist.

export default {
  name: "Dashboard",
  components: { documentEmailFetcher, folderTreeManager, fileDropUpload },
  data() {
    return {
      userService,
      showUploadDialog: false,
    }
  },
  methods: {
    async uploadMedia() {
      this.showUploadDialog = true
    },
    async reloadTreeManager() {
      this.showUploadDialog = false
      this.$refs.treeManager.init()
    },
    async loadEmails() {
      // Rufe die Methode der Kind-Komponente auf, die den Request startet
      await this.$refs.documentMailFetcher.fetchMails()
    },
  },
}
</script>

<style scoped>
.documents-page {
  --documents-primary: #009640;
  --documents-primary-soft: rgba(0, 150, 64, 0.14);
  --documents-surface: rgba(0, 0, 0, 0.2);
  --documents-border: rgba(255, 255, 255, 0.1);
  width: min(1540px, calc(100% - 40px));
  min-height: calc(100vh - 88px);
  margin: 0 auto;
  padding: 24px 0 32px;
  color: #f8fafc;
}

.documents-hero {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 18px;
  padding: 24px 26px;
  overflow: hidden;
  border: 1px solid var(--documents-border);
  border-radius: 24px;
  background: radial-gradient(circle at 88% 10%, rgba(0, 150, 64, 0.1), transparent 34%),
    linear-gradient(135deg, rgba(0, 0, 0, 0.28), rgba(255, 255, 255, 0.025));
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(18px);
}

.documents-hero::after {
  position: absolute;
  right: -46px;
  bottom: -78px;
  width: 230px;
  height: 230px;
  border: 1px solid rgba(0, 150, 64, 0.13);
  border-radius: 50%;
  content: "";
  pointer-events: none;
}

.hero-copy {
  display: flex;
  align-items: center;
  gap: 17px;
  min-width: 0;
}

.hero-icon {
  display: grid;
  flex: 0 0 auto;
  width: 52px;
  height: 52px;
  place-items: center;
  border: 1px solid rgba(0, 150, 64, 0.34);
  border-radius: 16px;
  background: rgba(0, 150, 64, 0.16);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 10px 24px rgba(0, 0, 0, 0.18);
}

.hero-icon .v-icon {
  font-size: 1.6875rem;
}

.hero-eyebrow {
  margin-bottom: 5px;
  color: #65ca8b;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-copy h1 {
  margin: 0;
  color: #f8fafc;
  font-size: clamp(1.5rem, 3vw, 2.125rem);
  font-weight: 750;
  line-height: 1.12;
  letter-spacing: -0.035em;
}

.hero-copy p {
  margin: 7px 0 0;
  color: #a7a7ac;
  font-size: 0.875rem;
  line-height: 1.5;
}

.hero-actions {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
}

.secondary-action,
.primary-action {
  min-height: 44px;
  padding-inline: 18px !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.secondary-action {
  border-color: rgba(255, 255, 255, 0.14) !important;
  color: #ececef !important;
  background: rgba(255, 255, 255, 0.035);
}

.primary-action {
  color: #ffffff !important;
  background: var(--documents-primary) !important;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
}

.secondary-action:hover,
.secondary-action:focus-visible {
  border-color: rgba(255, 255, 255, 0.24) !important;
  background: rgba(255, 255, 255, 0.075);
}

.primary-action:hover,
.primary-action:focus-visible {
  background: #00a848 !important;
}

.documents-workspace {
  height: clamp(620px, calc(100vh - 235px), 900px);
  min-height: 620px;
  overflow: hidden;
  border: 1px solid var(--documents-border);
  border-radius: 24px;
  background: var(--documents-surface);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(18px);
}

@media (max-width: 959px) {
  .documents-page {
    width: min(100% - 28px, 1540px);
    min-height: auto;
    padding-top: 16px;
  }

  .documents-hero {
    align-items: flex-start;
    padding: 20px;
  }

  .documents-workspace {
    height: auto;
    min-height: calc(100vh - 220px);
    overflow: visible;
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
  }
}

@media (max-width: 599px) {
  .documents-page {
    width: calc(100% - 20px);
    padding: 10px 0 24px;
  }

  .documents-hero {
    flex-direction: column;
    gap: 18px;
    margin-bottom: 12px;
    padding: 18px;
    border-radius: 20px;
  }

  .hero-icon {
    width: 46px;
    height: 46px;
    border-radius: 14px;
  }

  .hero-copy {
    align-items: flex-start;
  }

  .hero-copy p {
    font-size: 0.875rem;
  }

  .hero-actions {
    width: 100%;
  }

  .hero-actions .v-btn {
    flex: 1 1 auto;
    min-width: 0;
  }

  .secondary-action,
  .primary-action {
    padding-inline: 13px !important;
  }

  .documents-workspace {
    min-height: calc(100vh - 270px);
    border-radius: 20px;
  }
}
</style>
